import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminAuditLogs = () => {
  const [rawLogs, setRawLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchLogs();
  }, [debouncedSearch]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAuditLogs({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawLogs(response.data.logs);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = entityFilter
    ? rawLogs.filter((l) => (l.entity_type || '').toLowerCase().includes(entityFilter.toLowerCase()))
    : rawLogs;
  const total = filteredLogs.length;
  const logs = filteredLogs.slice((page - 1) * pageSize, page * pageSize);

  const filterOptions = [
    { value: 'user', label: 'User' },
    { value: 'kyc', label: 'KYC' },
    { value: 'deposit', label: 'Deposit' },
    { value: 'withdrawal', label: 'Withdrawal' },
    { value: 'trade', label: 'Trade' },
    { value: 'ticket', label: 'Ticket' },
  ];

  if (loading && rawLogs.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-audit-logs">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Audit Logs</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by admin, action, entity..."
        filterValue={entityFilter}
        onFilterChange={(v) => { setEntityFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All entities"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Admin</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Action</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Entity</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="table-row" data-testid={`log-row-${log.id}`}>
                  <td className="py-4">
                    <div>
                      <p className="text-foreground">{log.admin?.full_name || '—'}</p>
                      <p className="text-muted-foreground text-sm">{log.admin?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="py-4 text-foreground">{log.action}</td>
                  <td className="py-4 text-muted-foreground">{log.entity_type}</td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(log.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} pageSizeOptions={[25, 50, 100, 200]} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>
    </div>
  );
};

export default AdminAuditLogs;
